<template>
  <page :title="title">
    <Transition>
      <div v-if="!customer" key="customer" class="w-full">
        <FeatureFlag feature="parent_sub_account_ff_enabled">
          <SectionHeading class="mb-3" label="Parent Customer" />
          <ParentFields
            class="mb-3 full-width"
            :form-data="parentFormData"
            :disable-parent="disableParent"
            @update:parentId="
              (value) => {
                parentFormData.parent.id = value;
              }
            "
            @update:delegateBilling="
              (value) => {
                parentFormData.parent.delegate_billing = value;
              }
            "
          />
        </FeatureFlag>
        <SectionHeading class="mb-3" label="Contact Information" />
        <CustomerInfoForm ref="customerForm" class="mb-3"></CustomerInfoForm>
        <SectionHeading class="mb-3" label="Service Address" />
        <AddressForm
          ref="addressServiceForm"
          class="mb-3"
          hide-manual-entry-link
          hide-billing-address-toggle
          :isPrimary="isBillingSameAsService"
          :get-customer-data="getCustomerData"
        >
          <template #tax-fields>
            <TaxSettingsForm
              ref="taxSettingsServiceForm"
              class="mb-3"
              context-type="Customer"
              is-new
            />
          </template>
        </AddressForm>
        <CustomerTagForm ref="customerTagForm" />
        <div v-if="!parentFormData.parent.delegate_billing">
          <SectionHeading class="mb-3" label="Billing Address" />
          <ToggleSwitch
            id="billing-same-as-service-cb"
            v-model="isBillingSameAsService"
            name="billing-same-as-service-cb"
            label-on="Billing same as service"
            checked
            class="mb-4"
          />
          <AddressForm
            v-if="!isBillingSameAsService"
            ref="addressBillingForm"
            :addressType="'billing'"
            hide-billing-address-toggle
            class="mb-3"
            :isPrimary="!isBillingSameAsService"
          >
            <template #tax-fields>
              <TaxSettingsForm
                ref="taxSettingsBillingForm"
                class="mb-3"
                context-type="Customer"
                is-new
              />
            </template>
          </AddressForm>
        </div>
        <div
          class="flex justify-between items-center w-full gap-4 md:!flex-wrap flex-col-reverse md:flex-row sticky -bottom-8 bg-white pt-4 pb-8 md:pb-4 z-20 border-t border-solid customer-create-buttons-row"
        >
          <Button
            type="secondary"
            class="bg-red-700 w-full md:w-auto"
            @click="returnToCustomers()"
          >
            Cancel
          </Button>
          <div
            class="d-flex items-start md:items-center md:justify-end flex-col md:flex-row gap-3 w-full md:w-6/12 customer-create-save-buttons"
          >
            <Button
              outline
              class="whitespace-nowrap w-full md:w-auto overflow-visible"
              :saving="saving"
              :disabled="saving || continuing"
              data-cy="save-customer"
              @click="handleCreateCustomer(false)"
              >Save Customer</Button
            >
            <div class="mx-4 hidden md:block">OR</div>
            <Button
              :saving="continuing"
              :disabled="saving || continuing"
              class="whitespace-nowrap w-full md:w-auto overflow-visible"
              data-cy="save-customer-continue"
              @click="handleCreateCustomer(true)"
              >Save Customer & Schedule Appointment</Button
            >
          </div>
        </div>
      </div>
      <div v-else key="job" class="slide-left-entrance">
        <SectionHeading class="mb-3" label="Contact Information" />
        <div class="customer-info-cont">
          <CustomerInfo
            :customer="customer"
            :job="job"
            class="mb-4"
            hide-badges
          ></CustomerInfo>
        </div>
        <h3 class="mb-4">Schedule an Appointment</h3>
        <ScheduleJobModule
          v-if="serviceAddressData"
          ref="jobForm"
          :address="serviceAddressData"
        ></ScheduleJobModule>
        <div
          class="flex justify-end items-center w-full gap-4 flex-col-reverse md:flex-row sticky -bottom-8 bg-white py-4 z-20 border-t border-solid"
        >
          <Button
            :saving="saving"
            class="ml-4"
            :disabled="saving"
            data-cy="save-job"
            @click="handleCreateJob"
            >Schedule Appointment</Button
          >
        </div>
      </div>
    </Transition>
  </page>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { getQueryVariables } from "@/lib/url";

import Button from "@/components/Button.vue";
import Card from "@/components/Card.vue";
import FeatureFlag from "@/components/FeatureFlag.vue";
import Page from "@/components/Page.vue";
import SectionHeading from "@/components/SectionHeading.vue";
import ToggleSwitch from "@/components/ToggleSwitch.vue";
import Transition from "@/components/Transition.vue";
import AddressForm from "@/Customer/AddressModal/AddressForm.vue";
import CustomerInfoForm from "@/Customer/Create/CustomerInfoForm.vue";
import CustomerInfo from "@/Customer/CustomerInfo.vue";
import ParentFields from "@/Customer/CustomerModal/ParentFields.vue";
import { subCustomersPath } from "@/mixins/path_helpers";
import ScheduleJobModule from "@/ScheduleModal/ScheduleJobModule.vue";
import TaxSettingsForm from "@/Taxability/TaxSettingsForm.vue";
import useVuelidate from "@vuelidate/core";
import CustomerTagForm from "./CustomerTagForm.vue";

export default {
  components: {
    FeatureFlag,
    ParentFields,
    Page,
    Transition,
    Button,
    Card,
    CustomerInfoForm,
    AddressForm,
    CustomerInfo,
    ScheduleJobModule,
    TaxSettingsForm,
    SectionHeading,
    ToggleSwitch,
    CustomerTagForm
  },
  props: {
    title: {
      type: String,
      default: "New Customer"
    },
    parentId: {
      type: Number,
      default: null
    },
    disableParent: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  data() {
    return {
      saving: false,
      continuing: false,
      customer: null,
      parentFormData: {
        parent: {
          id: this.parentId,
          delegate_billing: false
        }
      },
      isBillingSameAsService: true,
      billingAddressData: null,
      serviceAddressData: null,
      tagIds: null
    };
  },
  computed: {
    ...mapState(["tenant"]),
    ...mapGetters(["featureEnabledForTenant"]),
    job() {
      return {
        address: this.serviceAddressData
      };
    },
    // Set when the dispatch board's customer lookup sent the user here;
    // save-and-schedule then returns to the board with the new
    // appointment's drawer open instead of landing on the job page.
    returnToSchedule() {
      return getQueryVariables().return_to === "schedule";
    }
  },
  mounted() {
    this.$store.dispatch("addBreadcrumb", { label: "Add Customer" });
    this.clearCurrentUser();
  },
  methods: {
    ...mapActions(["createCustomer", "createJob", "clearCurrentUser"]),
    async handleCreateCustomer(scheduleJob) {
      const isCustomerInfoValid = await this.$refs.customerForm.validate();
      const isServiceAddressValid =
        await this.$refs.addressServiceForm.validate();
      const isBillingAddressValid = !this.isBillingSameAsService
        ? await this.$refs.addressBillingForm.validate()
        : true;

      const userData = await this.$refs.customerForm.getFormData();

      const tagIds = await this.$refs.customerTagForm.getFormData();

      this.serviceAddressData =
        await this.$refs.addressServiceForm.getFormData();

      if (!this.isBillingSameAsService) {
        this.billingAddressData =
          await this.$refs.addressBillingForm.getFormData();
      }

      if (
        !isServiceAddressValid ||
        !isBillingAddressValid ||
        !isCustomerInfoValid
      ) {
        return;
      }

      const taxFormServiceData =
        await this.$refs.taxSettingsServiceForm.getFormData();
      this.serviceAddressData.tax_setting = {
        taxability: taxFormServiceData.taxability,
        tax_zone_id: taxFormServiceData.tax_zone_id,
        reason: taxFormServiceData.reason || null
      };

      if (!this.isBillingSameAsService) {
        const taxFormBillingData =
          await this.$refs.taxSettingsBillingForm.getFormData();
        this.billingAddressData.tax_setting = {
          taxability: taxFormBillingData.taxability,
          tax_zone_id: taxFormBillingData.tax_zone_id,
          reason: taxFormBillingData.reason || null
        };
      }

      if (
        userData &&
        this.serviceAddressData &&
        (this.isBillingSameAsService || this.billingAddressData)
      ) {
        // Determine which save button should spin
        const savingKey = scheduleJob ? "continuing" : "saving";
        const addressData = [this.serviceAddressData];
        if (this.billingAddressData) {
          addressData.push(this.billingAddressData);
        }

        this[savingKey] = true;
        this.createCustomer({
          ...userData,
          ...this.parentFormData,
          addresses: addressData,
          tags: tagIds
        })
          .then((res) => {
            const { id } = res.data;
            if (!scheduleJob) {
              // redirect to new customers page
              this.$router.push(this.userShowPath(id));
              return;
            }
            // Store new customer data in state
            this.customer = res.data;
            this.saving = false;
            this.continuing = false;
          })
          .catch(() => {
            this.saving = false;
            this.continuing = false;
          });
      }
    },
    async handleCreateJob() {
      const isValid = await this.v$.$validate();
      const isFormValid = await this.$refs.jobForm.validate();
      if (isValid && isFormValid) {
        this.saving = true;
        const { addresses } = this.customer;
        const payload = this.$refs.jobForm.getFormData();
        if (payload) {
          // use the last address
          const address = addresses.at(-1);

          this.createJob({
            link: { href: address.links.job_create, method: "POST" },
            payload
          })
            .then((res) => {
              const appointmentId = res.data.appointments?.[0]?.id;
              if (this.returnToSchedule && appointmentId) {
                this.$router.push(`/?appointment_id=${appointmentId}`);
              } else {
                this.$router.push(this.jobShowPath(res.data.id));
              }
            })
            .catch(() => {
              this.saving = false;
            });
        }
      }
    },
    returnToCustomers() {
      if (this.returnToSchedule) {
        this.$router.push("/");
      } else if (this.parentId) {
        this.$router.push(subCustomersPath(this.parentId));
      } else {
        this.$router.push("/customers");
      }
    },
    getCustomerData() {
      return this.$refs.customerForm.getFormData();
    }
  }
};
</script>

<style lang="scss" scoped>
.customer-form-card {
  //max-width: 815px;
}

.customer-info-cont {
  min-width: 290px;
}

.slide-left-entrance {
  animation: slideLeftEntrance 250ms ease-in-out forwards;
}
@keyframes slideLeftEntrance {
  0% {
    transform: translateX(20%);
    opacity: 0;
  }
  100% {
    transform: translateX(0%);
    opacity: 1;
  }
}

@media screen and (max-width: 915px) and (min-width: 768px) {
  .customer-create-buttons-row {
    font-size: 0.8em;
  }
  .customer-create-save-buttons {
    gap: 0px !important;
  }
}
</style>
